
/**
 * @file This custom hook manages the integration with Excel files, handling the linking, data parsing, and state management.
 * It abstracts away the complexities of fetching, downloading, and processing Excel data, providing a simplified interface
 * for components that need to interact with Excel files.
 */
import { useState, useEffect, useMemo } from 'react';
import { useGlobalProvider } from '../contexts/GlobalProvider';
import { useAuth } from '../contexts/AuthContext';
import { getItemVersions } from '../api/versionsService';
import { fetchDownloadUrl } from '../api/fetchDownloadUrl';
import { extractBucketAndObjectKeys } from '../utilities/storageUtils';
import { readExcelFile, getExcelHeaders, getProcessedExcelRows } from '../api/excelService';

/**
 * Custom hook for managing Excel file integration.
 * @returns {{excelHeaders: Array, excelRows: Array, selectedExcelColumn: string, isExcelLinked: boolean, linkedExcelFileName: string, handleLinkExcelClick: Function, handleExcelColumnSelect: Function, handleClearExcelLink: Function, excelOptions: object, loadExcelSettings: Function}}
 */
const useExcelIntegration = () => {
    const { selectedProjectId, accCurrentNode } = useGlobalProvider();
    const { getAccessToken } = useAuth();

    const [excelHeaders, setExcelHeaders] = useState([]);
    const [excelRows, setExcelRows] = useState([]);
    const [selectedExcelColumn, setSelectedExcelColumn] = useState('');
    const [isExcelLinked, setIsExcelLinked] = useState(false);
    const [linkedExcelFileName, setLinkedExcelFileName] = useState('');

    const EXCEL_URN_KEY = `excel_document_urn_${selectedProjectId}`;
    const EXCEL_COLUMN_KEY = `excel_selected_column_${selectedProjectId}`;

    const saveExcelSettings = (documentUrn, fileName, selectedColumn = '') => {
        try {
            const settings = {
                documentUrn,
                fileName,
                selectedColumn,
                timestamp: Date.now()
            };
            localStorage.setItem(EXCEL_URN_KEY, JSON.stringify(settings));
            if (selectedColumn) {
                localStorage.setItem(EXCEL_COLUMN_KEY, selectedColumn);
            }
        } catch (error) {
            console.error('Error saving Excel settings to localStorage:', error);
        }
    };

    const loadExcelSettings = () => {
        try {
            const savedSettings = localStorage.getItem(EXCEL_URN_KEY);
            const savedColumn = localStorage.getItem(EXCEL_COLUMN_KEY);

            if (savedSettings) {
                const settings = JSON.parse(savedSettings);
                return {
                    ...settings,
                    selectedColumn: savedColumn || settings.selectedColumn || ''
                };
            }
        } catch (error) {
            console.error('Error loading Excel settings from localStorage:', error);
        }
        return null;
    };

    const clearExcelSettings = () => {
        try {
            localStorage.removeItem(EXCEL_URN_KEY);
            localStorage.removeItem(EXCEL_COLUMN_KEY);
        } catch (error) {
            console.error('Error clearing Excel settings from localStorage:', error);
        }
    };

    /**
     * Fetches and parses an Excel file from a given URN.
     * @param {string} itemUrn - The URN of the Excel file to fetch.
     * @returns {{headers: Array, rows: Array}}
     */
    const linkExcelFileByUrn = async (itemUrn) => {
        const accessToken = await getAccessToken();
        const versionsResponse = await getItemVersions({
            selectedProjectId,
            itemUrn,
            accessToken
        });

        if (!versionsResponse.data || versionsResponse.data.length === 0) {
            throw new Error('No versions found for the selected document');
        }

        const latestVersion = versionsResponse.data[0];
        const storageId = latestVersion.relationships?.storage?.data?.id;
        if (!storageId) {
            throw new Error('No storage information found for the latest version');
        }

        const { bucketKey, objectKey } = extractBucketAndObjectKeys(storageId);
        const downloadResponse = await fetchDownloadUrl({
            accessToken,
            bucketKey,
            objectKey
        });

        const fileResponse = await fetch(downloadResponse.url);
        if (!fileResponse.ok) {
            throw new Error(`Failed to download file: ${fileResponse.status} ${fileResponse.statusText}`);
        }

        const blob = await fileResponse.blob();
        const file = new File([blob], 'document.xlsx', { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

        const result = await readExcelFile(file);
        return {
            headers: getExcelHeaders(result),
            rows: getProcessedExcelRows(result)
        };
    };

    /**
     * Restores the Excel settings from local storage.
     */
    const restoreExcelSettings = async () => {
        const savedSettings = loadExcelSettings();
        if (!savedSettings) return;

        try {
            const { headers, rows } = await linkExcelFileByUrn(savedSettings.documentUrn);

            if (savedSettings.selectedColumn && !headers.includes(savedSettings.selectedColumn)) {
                localStorage.removeItem(EXCEL_COLUMN_KEY);
                setSelectedExcelColumn('');
            } else {
                setSelectedExcelColumn(savedSettings.selectedColumn || '');
            }

            setExcelHeaders(headers);
            setExcelRows(rows);
            setIsExcelLinked(true);
            setLinkedExcelFileName(savedSettings.fileName);

        } catch (error) {
            console.error('Error restoring Excel settings:', error);
            clearExcelSettings();
            setIsExcelLinked(false);
            setLinkedExcelFileName('');
            setExcelHeaders([]);
            setExcelRows([]);
            setSelectedExcelColumn('');
        }
    };

    const handleLinkExcelClick = async () => {
        if (!accCurrentNode) {
            alert('Please select a document from the document tree first');
            return;
        }

        try {
            const { headers, rows } = await linkExcelFileByUrn(accCurrentNode.itemUrn);

            setExcelHeaders(headers);
            setExcelRows(rows);
            setSelectedExcelColumn('');
            setIsExcelLinked(true);
            const fileName = accCurrentNode.displayName || accCurrentNode.name || 'Excel file';
            setLinkedExcelFileName(fileName);

            saveExcelSettings(accCurrentNode.itemUrn, fileName);

        } catch (error) {
            console.error('Error linking Excel from ACC:', error);
            alert(`Error linking Excel file: ${error.message}`);
        }
    };

    const handleExcelColumnSelect = (event) => {
        const newColumn = event.target.value;
        setSelectedExcelColumn(newColumn);

        if (isExcelLinked && accCurrentNode) {
            saveExcelSettings(accCurrentNode.itemUrn, linkedExcelFileName, newColumn);
        }
    };
    
    const handleClearExcelLink = () => {
        setIsExcelLinked(false);
        setLinkedExcelFileName('');
        setExcelHeaders([]);
        setExcelRows([]);
        setSelectedExcelColumn('');
        clearExcelSettings();
    };

    /**
     * Effect hook to restore Excel settings when the project changes.
     */
    useEffect(() => {
        if (selectedProjectId) {
            const savedSettings = loadExcelSettings();
            if (savedSettings && savedSettings.documentUrn) {
                restoreExcelSettings();
            } else {
                // Clear state when switching to a project with no saved Excel settings
                setIsExcelLinked(false);
                setLinkedExcelFileName('');
                setExcelHeaders([]);
                setExcelRows([]);
                setSelectedExcelColumn('');
            }
        }
    }, [selectedProjectId]);

    const excelOptions = useMemo(() => ({
        excelData: excelRows,
        linkColumn: selectedExcelColumn
    }), [excelRows, selectedExcelColumn]);

    return {
        excelHeaders,
        excelRows,
        selectedExcelColumn,
        isExcelLinked,
        linkedExcelFileName,
        handleLinkExcelClick,
        handleExcelColumnSelect,
        handleClearExcelLink,
        excelOptions,
        loadExcelSettings
    };
};

export default useExcelIntegration;
